import React from 'react';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';

import {
  AMBIGUITY_LABELS,
  domainBadgeType,
  domainDisplayName,
} from './constants';

// The coverage chips + ambiguity clusters, extracted from the report list so the
// other surfaces can show the same summary without the full roster. A `name · 0` chip
// is not thin coverage — it means the domain skill never loads at all.

const AmbiguityDiv = styled.div`
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 8px;
  background-color: var(--color-badge-warning-bg);
  border: 1px solid var(--color-badge-warning-border);
`;

const CoverageSummary = ({ coverage }: { coverage: AiReady.Coverage }) => (
  <>
    <Row
      centerAlign
      style={{ gap: 8, marginTop: 8, marginBottom: 16, flexWrap: 'wrap' }}
      data-testid="ai-ready-coverage"
    >
      {coverage.byDomain.length === 0 && (
        <Typography.Body type="Body 12" color="var(--color-text-secondary)">
          No domains — no knowledge-base version resolves for this account.
        </Typography.Body>
      )}
      {coverage.byDomain.map((domain) => (
        <Badge
          key={domain.domain}
          badgeType={domainBadgeType({
            reportCount: domain.reportCount,
            isShippedByKb: domain.isShippedByKb,
          })}
          text={`${domainDisplayName(domain.domain)} · ${domain.reportCount}`}
        />
      ))}
    </Row>

    {coverage.clusters.length > 0 && (
      <AmbiguityDiv data-testid="ai-ready-ambiguity">
        {coverage.clusters.map((cluster) => (
          <Typography.Body
            key={cluster.name}
            type="Body 12"
            color="var(--color-text-secondary)"
          >
            <strong>{cluster.name}</strong> ×{cluster.members.length} in{' '}
            {domainDisplayName(cluster.domain)} (
            {AMBIGUITY_LABELS[cluster.severity]}) —{' '}
            {cluster.members
              .map(
                (member) =>
                  `${member.reportNames.join(', ') || 'unknown report'}${
                    member.datasets.length
                      ? ` [${member.datasets.join(', ')}]`
                      : ''
                  }`,
              )
              .join(' · ')}
          </Typography.Body>
        ))}
      </AmbiguityDiv>
    )}
  </>
);

export default CoverageSummary;
